/**
 * <MovementEditDialog /> — universal movement editor.
 *
 * Lets the user adjust any field of a historical movement
 * (income / expense / transfer) including type, account(s), amounts
 * and FX. The server-side ``PUT /api/movements/{id}`` reconciles cash
 * balances automatically (reverts the old effect, applies the new
 * one), so this dialog is a thin form on top of that endpoint.
 *
 * Extracted from ``History.jsx`` for clarity — same component, same
 * data-testids, just lives in its own file now.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Paperclip, Trash2, Upload, CreditCard, Save } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { api } from "../lib/api";
import { pushHighlight } from "../lib/highlights";
import { AttachmentBadge } from "./AttachmentBadge";
import { AccountPickerField } from "./AccountPickerField";
import { z } from "zod";
import { useZodForm, FormField, SubmitButton } from "./form-ux";

export default function MovementEditDialog({ m, open, onClose, accounts, isEs }) {
  const qc = useQueryClient();
  const isFx = !!m.original_currency && m.original_amount != null;

  const Schema = z.object({
    type: z.enum(["income", "expense", "transfer"]),
    amount: z.string().refine(
      (v) => v !== "" && !Number.isNaN(parseFloat(v)) && parseFloat(v) > 0,
      { message: isEs ? "Monto inválido. Debe ser un número mayor a 0." : "Invalid amount. Must be a number greater than 0." },
    ),
    original_currency: z.string().optional(),
    description: z.string(),
    account_id: z.string().optional(),
    source_debt_id: z.string().optional(),
    to_account_id: z.string().optional(),
    to_amount: z.string().optional(),
    fx_rate_used: z.string().optional(),
    fx_source: z.string().optional(),
    notes: z.string().optional(),
  });

  const form = useZodForm(Schema, {
    type: m.type,
    amount: String(isFx ? m.original_amount : (m.amount ?? "")),
    original_currency: m.original_currency || "",
    description: m.description || "",
    account_id: m.account_id || "",
    source_debt_id: m.source_debt_id || "",
    to_account_id: m.to_account_id || "",
    to_amount: m.to_amount != null ? String(m.to_amount) : "",
    fx_rate_used: m.fx_rate_used != null ? String(m.fx_rate_used) : "",
    fx_source: m.fx_source || "",
    notes: m.notes || "",
  }, { formId: `edit-movement-${m.id}` });

  const { data: debts = [] } = useQuery({
    queryKey: ["debts-picker"],
    queryFn: async () => {
      const r = (await api.get("/debts")).data;
      return Array.isArray(r) ? r : (r?.rows || []);
    },
    staleTime: 60_000,
  });
  const REVOLVING_KINDS = new Set(["credit_card", "line_of_credit", "revolving"]);
  const revolvingDebts = debts.filter((d) => REVOLVING_KINDS.has(d.kind));

  const source = accounts.find((a) => a.id === form.values.account_id);
  const dest = accounts.find((a) => a.id === form.values.to_account_id);
  const crossCurrency =
    form.values.type === "transfer" && source && dest && source.currency !== dest.currency;

  const update = useMutation({
    mutationFn: async (body) => (await api.put(`/movements/${m.id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["cash-accounts"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["movements"] });
      pushHighlight("movement", m.id);
      toast.success(isEs ? "Movimiento actualizado" : "Movement updated");
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "error"),
  });

  const submit = form.handleSubmit(async (values) => {
    const amt = parseFloat(values.amount);
    const body = {
      type: values.type,
      amount: amt,
      description: values.description,
      notes: values.notes,
      original_currency: values.original_currency || null,
    };
    if (values.source_debt_id) {
      body.source_debt_id = values.source_debt_id;
      body.account_id = null;
    } else if (values.account_id) {
      body.account_id = values.account_id;
      body.source_debt_id = null;
    } else {
      body.account_id = null;
      body.source_debt_id = null;
    }
    if (values.type === "transfer") {
      body.to_account_id = values.to_account_id;
      if (values.to_amount) body.to_amount = parseFloat(values.to_amount);
      if (values.fx_rate_used) body.fx_rate_used = parseFloat(values.fx_rate_used);
    }
    await update.mutateAsync(body);
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`edit-movement-dialog-${m.id}`}>
        <DialogHeader>
          <DialogTitle>{isEs ? "Editar movimiento" : "Edit movement"}</DialogTitle>
          <DialogDescription>
            {isEs
              ? "Los reportes y saldos se actualizan automáticamente."
              : "Reports and balances are updated automatically."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="edit-movement-form" noValidate>
          <FormField label={isEs ? "Tipo" : "Type"} name="type">
            <Select value={form.values.type} onValueChange={(v) => form.setValue("type", v)}>
              <SelectTrigger data-testid="edit-movement-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="income">{isEs ? "Ingreso" : "Income"}</SelectItem>
                <SelectItem value="expense">{isEs ? "Gasto" : "Expense"}</SelectItem>
                <SelectItem value="transfer">{isEs ? "Traslado" : "Transfer"}</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          <FormField label={isEs ? "Descripción" : "Description"} name="description">
            <FormField.Input
              data-testid="edit-movement-description"
              {...form.getFieldProps("description")}
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              label={isFx
                ? (isEs ? `Monto (${form.values.original_currency || m.original_currency})` : `Amount (${form.values.original_currency || m.original_currency})`)
                : (isEs ? "Monto" : "Amount")}
              name="amount"
              required
              hint={isFx
                ? (isEs
                    ? `Valor original en ${form.values.original_currency || m.original_currency}. La conversión a ${m.currency || ""} se actualiza al guardar.`
                    : `Original value in ${form.values.original_currency || m.original_currency}. Conversion to ${m.currency || ""} refreshes on save.`)
                : undefined}
              error={form.errors.amount && (form.touched.amount || form.submitCount > 0) ? form.errors.amount : undefined}
            >
              <FormField.Input
                type="number"
                step="any"
                min="0"
                inputMode="decimal"
                autoComplete="off"
                data-testid="edit-movement-amount"
                {...form.getFieldProps("amount")}
              />
            </FormField>
            <div>
              <AccountPickerField
                label={form.values.type === "transfer"
                  ? (isEs ? "Cuenta origen" : "Source")
                  : (isEs ? "Cuenta" : "Account")}
                name="edit-movement-account"
                accounts={accounts}
                value={form.values.account_id}
                onChange={(v) => {
                  form.setValue("account_id", v);
                  if (v) form.setValue("source_debt_id", "");
                }}
                placeholder="—"
                isEs={isEs}
                testid="edit-movement-account-field"
              />
              {form.values.type !== "transfer" && revolvingDebts.length > 0 && (
                <div className="mt-2">
                  <Label className="text-[10px] flex items-center gap-1 text-rose-700">
                    <CreditCard className="h-3 w-3" />
                    {isEs ? "O pagar con tarjeta / línea" : "Or pay with card / line"}
                  </Label>
                  <Select
                    value={form.values.source_debt_id || "__none__"}
                    onValueChange={(v) => {
                      form.setValue("source_debt_id", v === "__none__" ? "" : v);
                      if (v !== "__none__") form.setValue("account_id", "");
                    }}
                  >
                    <SelectTrigger
                      className="mt-1"
                      data-testid="edit-movement-debt-trigger"
                    >
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        value="__none__"
                        data-testid="edit-movement-debt-opt-none"
                      >
                        — {isEs ? "Sin tarjeta" : "No card"} —
                      </SelectItem>
                      {revolvingDebts.map((d) => (
                        <SelectItem
                          key={d.id}
                          value={d.id}
                          data-testid={`edit-movement-debt-opt-${d.id}`}
                        >
                          {d.name}
                          {d.currency ? ` · ${d.currency}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.values.source_debt_id && (
                    <p className="text-[10px] text-rose-600 mt-1">
                      {form.values.type === "income"
                        ? (isEs
                            ? "Recibir en tarjeta disminuye el saldo pendiente por este monto."
                            : "Receiving into the card reduces the outstanding balance by this amount.")
                        : (isEs
                            ? "Al pagar con tarjeta, el saldo de la tarjeta crece por este monto."
                            : "Paying with a card grows its balance by this amount.")}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {form.values.type === "transfer" && (
            <>
              <div>
                <AccountPickerField
                  label={isEs ? "Cuenta destino" : "Destination"}
                  name="edit-movement-to-account"
                  accounts={accounts}
                  value={form.values.to_account_id}
                  onChange={(v) => form.setValue("to_account_id", v)}
                  excludeIds={[form.values.account_id]}
                  placeholder="—"
                  isEs={isEs}
                  testid="edit-movement-to-account-field"
                />
              </div>
              {crossCurrency && (
                <div className="grid grid-cols-2 gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <FormField
                    label={isEs ? `Monto destino (${dest?.currency})` : `Destination amount (${dest?.currency})`}
                    name="to_amount"
                  >
                    <FormField.Input
                      type="number"
                      step="any"
                      min="0"
                      inputMode="decimal"
                      data-testid="edit-movement-to-amount"
                      value={form.values.to_amount}
                      onChange={(e) => {
                        form.setValue("to_amount", e.target.value);
                        form.setValue("fx_rate_used", "");
                      }}
                    />
                  </FormField>
                  <FormField
                    label={isEs ? "Tasa (1→?)" : "Rate (1→?)"}
                    name="fx_rate_used"
                  >
                    <FormField.Input
                      type="number"
                      step="any"
                      min="0"
                      inputMode="decimal"
                      data-testid="edit-movement-fx-rate"
                      value={form.values.fx_rate_used}
                      onChange={(e) => {
                        form.setValue("fx_rate_used", e.target.value);
                        form.setValue("to_amount", "");
                      }}
                    />
                  </FormField>
                </div>
              )}
            </>
          )}

          <ReceiptAttachField movement={m} isEs={isEs} />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="edit-movement-cancel"
            >
              {isEs ? "Cancelar" : "Cancel"}
            </Button>
            <SubmitButton
              submitting={form.submitting || update.isPending}
              loadingText={isEs ? "Guardando…" : "Saving…"}
              testid="edit-movement-save"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="h-4 w-4" />
              {isEs ? "Guardar" : "Save"}
            </SubmitButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/**
 * ReceiptAttachField — iter-169y.
 *
 * Renders the current receipt (if any) + an upload control. Uploading
 * a new file replaces the previous attachment. Used inside the
 * MovementEditDialog so users who recorded a movement manually can
 * still keep a digital copy of the original receipt/voucher.
 *
 * Visual rules:
 *   • No attachment yet → minimal "📎 Adjuntar recibo" button.
 *   • Has attachment   → thumbnail (via AttachmentBadge) + "Reemplazar"
 *                        and "Quitar" controls.
 */
function ReceiptAttachField({ movement, isEs }) {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  // The list endpoint already ships `attachment` on each movement; we
  // mirror it locally so the dialog reflects upload/replace/delete
  // without waiting for the next list refetch.
  const [attachment, setAttachment] = useState(movement.attachment || null);
  const [uploading, setUploading] = useState(false);

  // Map UI kind → backend route segment.
  const kind = movement.kind || "movement";

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // Replace strategy: if an attachment exists, delete it FIRST so we
      // don't leave orphans in GridFS. Best-effort — if delete fails the
      // new upload still proceeds.
      if (attachment?.id) {
        try { await api.delete(`/attachments/${attachment.id}`); } catch (_) { /* noop */ }
      }
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/attachments/${kind}/${movement.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment(data.attachment);
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["movements"] });
      toast.success(isEs ? "Recibo adjuntado" : "Receipt attached");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.startsWith("unsupported_mime")) {
        toast.error(isEs ? "Tipo de archivo no soportado." : "Unsupported file type.");
      } else if (detail === "file_too_large") {
        toast.error(isEs ? "Máximo 8 MB." : "Max 8 MB.");
      } else {
        toast.error(isEs ? "No se pudo adjuntar." : "Could not attach.");
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!attachment?.id) return;
    try {
      await api.delete(`/attachments/${attachment.id}`);
      setAttachment(null);
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["movements"] });
      toast.success(isEs ? "Recibo eliminado" : "Receipt removed");
    } catch (_) {
      toast.error(isEs ? "No se pudo eliminar." : "Could not remove.");
    }
  };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 flex items-center gap-3"
      data-testid="edit-movement-attach-field"
    >
      <input /* design-system: ALLOW legacy-todo */
        // design-system: ALLOW file picker — hidden trigger for the visible Button
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,application/pdf"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid="edit-movement-attach-input"
      />
      {attachment ? (
        <>
          <AttachmentBadge attachment={attachment} testid={`edit-movement-attach-thumb-${movement.id}`} />
          <div className="text-xs text-slate-600 flex-1 min-w-0">
            <div className="font-medium truncate">
              {attachment.mime_type === "application/pdf" ? "PDF" : isEs ? "Comprobante" : "Receipt"}
            </div>
            <div className="text-[10px] text-slate-500">
              {isEs ? "Click en la miniatura para abrir." : "Click thumbnail to open."}
            </div>
          </div>
          <Button
            type="button" variant="outline" size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="gap-1 text-xs"
            data-testid="edit-movement-attach-replace"
          >
            <Upload className="h-3 w-3" />
            {isEs ? "Reemplazar" : "Replace"}
          </Button>
          <Button
            type="button" variant="ghost" size="sm"
            onClick={remove}
            disabled={uploading}
            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 gap-1 text-xs"
            data-testid="edit-movement-attach-remove"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </>
      ) : (
        <>
          <span className="h-9 w-9 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center shrink-0">
            <Paperclip className="h-4 w-4" />
          </span>
          <div className="text-xs text-slate-600 flex-1">
            <div className="font-medium">
              {isEs ? "Sin recibo adjunto" : "No receipt attached"}
            </div>
            <div className="text-[10px] text-slate-500">
              {isEs ? "Imagen o PDF, hasta 8 MB." : "Image or PDF, up to 8 MB."}
            </div>
          </div>
          <Button
            type="button" variant="outline" size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="gap-1.5 text-xs"
            data-testid="edit-movement-attach-add"
          >
            {uploading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Paperclip className="h-3 w-3" />}
            {isEs ? "Adjuntar recibo" : "Attach receipt"}
          </Button>
        </>
      )}
    </div>
  );
}
